import React, { useRef, useState, useEffect } from 'react';
import { Element } from 'react-scroll';
import clsx from 'clsx';
import PlayIcon from '~/components/shared/video/PlayIcon';
import ConditionalWrapper from '~/components/shared/conditionalWrapper/ConditionalWrapper';
import AnimationPlayer from '~/components/shared/animationPlayer/AnimationPlayer';
import DividerLine from '../../shared/dividers/DividerLine';

const DetailedContentItem = ({
  guid,
  title,
  description,
  textPosition,
  dividerLineColor,
  isAnimated,
  restrictHeight,
  image,
  imageMobile,
  imageUrl,
  imageUrlTarget,
  altText,
  videoUrl,
  playIconColor,
  animation,
  prettyUrlTitle,
  addContainer,
  setSelectedContent
}) => {
  const container = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const hasAnimation = animation && animation.animationUrl;
  const hasImage = image && image.url;
  const hasMedia = hasAnimation || hasImage;

  useEffect(() => {
    const { current } = container;

    if (!current) return undefined;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => setIsVisible((prev) => prev || entry.isIntersecting));
    });
    observer.observe(current);

    return () => observer.unobserve(current);
  }, []);

  return (
    <div
      className={clsx('section-gutter-half', !isAnimated && addContainer && 'container-fluid')}
      ref={container}
    >
      <ConditionalWrapper
        condition={prettyUrlTitle}
        wrapper={(children) => <Element name={prettyUrlTitle}>{children}</Element>}
      >
        <div className={clsx('section-intro', textPosition, isAnimated && 'animated')}>
          <div className={clsx('content', isAnimated && isVisible && 'visible')}>
            <h2>
              <span dangerouslySetInnerHTML={{ __html: title }} />
            </h2>
            <div dangerouslySetInnerHTML={{ __html: description }} />
          </div>

          {hasMedia && <DividerLine orientation="vertical" color={dividerLineColor} />}

          <div className={clsx('content-right', { media: hasMedia && !videoUrl })}>
            <ConditionalWrapper
              condition={videoUrl}
              wrapper={(children) => (
                <button
                  className="btn-no-style position-relative"
                  type="button"
                  onClick={() => setSelectedContent(guid)}
                >
                  <PlayIcon iconColor={playIconColor} />
                  {children}
                </button>
              )}
            >
              {hasImage && (
                <ConditionalWrapper
                  condition={imageUrl && !videoUrl}
                  wrapper={(children) => (
                    <a
                      href={imageUrl}
                      target={imageUrlTarget}
                      title={`Link for ${altText || title}`}
                    >
                      {children}
                    </a>
                  )}
                >
                  <picture>
                    {imageMobile.url && (
                      <source
                        media="(max-width: 1024px)"
                        srcSet={encodeURI(imageMobile.url)}
                        height={imageMobile.height}
                        width={imageMobile.width}
                      />
                    )}
                    <img
                      alt={altText || `Media for ${title}`}
                      className={clsx({
                        'restrict-height': restrictHeight
                      })}
                      src={image.url}
                      height={image.height}
                      width={image.width}
                      loading="lazy"
                    />
                  </picture>
                </ConditionalWrapper>
              )}
              {hasAnimation && <AnimationPlayer {...animation} />}
            </ConditionalWrapper>
          </div>
        </div>
      </ConditionalWrapper>
    </div>
  );
};

export default DetailedContentItem;
